/**
 * Returns the maximum value of a numeric array without using the spread
 * operator (`Math.max(...arr)` throws a RangeError on large arrays) and
 * without `Array.reduce` (flagged by SonarQube rule S6959). Safe on empty
 * arrays, in which case it returns null.
 */
export function getMaxValue(values) {
    if (!values || values.length === 0) {
        return null;
    }
    let max = values[0];
    for (let i = 1; i < values.length; i++) {
        if (values[i] > max) {
            max = values[i];
        }
    }
    return max;
}

/**
 * Returns the minimum value of a numeric array. See getMaxValue for the
 * rationale behind avoiding spread and reduce.
 */
export function getMinValue(values) {
    if (!values || values.length === 0) {
        return null;
    }
    let min = values[0];
    for (let i = 1; i < values.length; i++) {
        if (values[i] < min) {
            min = values[i];
        }
    }
    return min;
}

/**
 * Linearly interpolates the magnitude at a target frequency between two
 * adjacent spectrum points (lower and upper).
 */
function interpolateMagnitude(target, lower, upper) {
    if (upper.frequency === lower.frequency) {
        return lower.magnitude;
    }
    const t = (target - lower.frequency) / (upper.frequency - lower.frequency);
    return lower.magnitude + t * (upper.magnitude - lower.magnitude);
}

export function getSpecialFrequenciesMagnitudes(
    targetFreq,
    spectrum
) {
    if (!spectrum || spectrum?.length === 0) {
        return null;
    }

    // Find the index of the first spectrum point whose frequency exceeds targetFreq
    const upperIdx = spectrum.findIndex((p) => p.frequency > targetFreq);

    if (upperIdx === -1) {
        // Target is beyond the last spectrum point – clamp to last value
        return spectrum[spectrum.length - 1].magnitude;
    }
    if (upperIdx === 0) {
        // Target is below the first spectrum point – clamp to first value
        return spectrum[0].magnitude;
    }

    const lower = spectrum[upperIdx - 1];
    const upper = spectrum[upperIdx];
    const interpolated = interpolateMagnitude(targetFreq, lower, upper);

    return interpolated;
}

// Retrieves the spectrum data for a given sensor axis from the FFT data
export function getSpectrumDataForFrequency(fftData, sensorAxisName) {
    const sensorData = fftData?.dataValues?.find((sensor) => sensor.sensorAxisName === sensorAxisName);
    return sensorData?.sensorAxisDataValues || [];
}
